import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  User,
  Calendar,
  Eye,
  Loader2,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { analyticsAPI } from "@/services/api";
import { format } from "date-fns";
import config from "../../config/config";
import ExamDetailView from "@/components/admin/ExamDetailView";
import ModernPageTemplate, { 
  ModernSearch, 
  ModernButton, 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

const Analytics = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Constants
  const EXAMS_PER_PAGE = 3; // Reduced for modal
  const CANDIDATES_PER_PAGE = 5; // For table pagination
  
  // State management
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showExamDetail, setShowExamDetail] = useState(false);
  const [examResultsPage, setExamResultsPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(CANDIDATES_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Filter states
  const [tempFilterType, setTempFilterType] = useState(filterType);

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      // Check if we're on desktop (lg breakpoint)
      if (window.innerWidth >= 1024) {
        // Look for the main content container to determine sidebar state
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            // Sidebar is collapsed (68px width)
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            // Sidebar is expanded (256px width)
            footer.style.left = '256px';
          }
        }
      } else {
        // Mobile/tablet - full width
        footer.style.left = '0px';
      }
    };

    // Initial adjustment
    adjustFooterPosition();

    // Listen for window resize
    window.addEventListener('resize', adjustFooterPosition);

    // Use MutationObserver to watch for class changes on the main content
    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  // Query for candidates list - API returns candidates sorted by creation date (newest first)
  const {
    data: candidatesData,
    isLoading: candidatesLoading,
    isError: candidatesError,
  } = useQuery({
    queryKey: ["analytics-candidates", currentPage, searchTerm, filterType, isPaginationEnabled, rowsPerPage],
    queryFn: () =>
      analyticsAPI.getCandidates({
        page: isPaginationEnabled ? currentPage : 1,
        limit: isPaginationEnabled ? rowsPerPage : 1000, // Large number to get all records
        search: searchTerm,
        filter: filterType,
      }),
    enabled: true,
  });

  // Query for candidate details
  const {
    data: candidateDetails,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useQuery({
    queryKey: ["candidate-details", selectedCandidateId],
    queryFn: () => analyticsAPI.getCandidateDetails(selectedCandidateId),
    enabled: !!selectedCandidateId && showModal,
  });

  // Query for exam detail data
  const {
    data: examDetailData,
    isLoading: examDetailLoading,
  } = useQuery({
    queryKey: ["exam-detail", selectedSessionId],
    queryFn: () => analyticsAPI.getExamSessionDetail(selectedSessionId),
    enabled: !!selectedSessionId && showExamDetail,
  });

  // Reset states when candidate changes
  useEffect(() => {
    setSelectedSessionId("");
    setShowExamDetail(false);
    setExamResultsPage(1);
  }, [selectedCandidateId]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search/filter changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm, filterType]);

  // Refresh function that properly invalidates queries and resets state
  const handleRefresh = async () => {
    // Reset all state to initial values
    setCurrentPage(1);
    setSortField('');
    setSortOrder('asc');
    setSearchTerm('');
    setFilterType('all');
    setSelectedCandidateId('');
    setSelectedSessionId('');
    setShowExamDetail(false);
    setShowModal(false);
    setExamResultsPage(1);
    setShowFilterModal(false);
    
    // Invalidate and refetch all related queries
    await queryClient.invalidateQueries({ queryKey: ["analytics-candidates"] });
    await queryClient.invalidateQueries({ queryKey: ["candidate-details"] });
    await queryClient.invalidateQueries({ queryKey: ["exam-detail"] });
    
    // Show success message
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortCandidates = (candidates, sortField, sortOrder) => {
    // If no sorting is applied, return candidates in their original order (newest first from API)
    // This ensures newly created candidates appear at the top across all pages
    if (!sortField) return candidates;

    return [...candidates].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdAt') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleCandidateView = (candidateId) => {
    setSelectedCandidateId(candidateId);
    setShowModal(true);
    setShowExamDetail(false);
  };

  const handleExamSelect = (examId, resultId) => {
    setSelectedSessionId(resultId);
    setShowExamDetail(true);
  };

  const handleBackToOverview = () => {
    setShowExamDetail(false);
    setSelectedSessionId("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
  };

  const handleFilterToggle = () => {
    setTempFilterType(filterType); // Reset temp values when opening
    setShowFilterModal(!showFilterModal);
  };

  const handleApplyFilters = () => {
    setFilterType(tempFilterType);
    setShowFilterModal(false);
    toast({
      title: "Filters Applied",
      description: "Candidate filters have been updated successfully",
    });
  };

  const handleClearFilters = () => {
    setTempFilterType('all');
  };

  const handleCancelFilters = () => {
    setTempFilterType(filterType); // Reset to current values
    setShowFilterModal(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handlePaginationToggle = (checked) => {
    setIsPaginationEnabled(checked);
    setCurrentPage(1); // Reset to first page when toggling pagination
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCandidateId("");
    setShowExamDetail(false);
    setExamResultsPage(1);
  };

  // Direct PDF download without redirects
  const downloadPDF = async (url, filename) => {
    try {
      setIsDownloading(true);
      const token = localStorage.getItem("token");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download report");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Success",
        description: "Report downloaded successfully",
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      toast({
        title: "Error",
        description: "Failed to download report",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedCandidateId) return;

    const candidate = candidateDetails?.data?.candidate;
    const filename = `candidate-report-${candidate?.name || "unknown"}.pdf`;
    await downloadPDF(
      `${config.API_URL}/analytics/export/${selectedCandidateId}`,
      filename
    );
  };

  const handleExamDetailExport = async () => {
    if (!selectedSessionId) return;

    const candidate = examDetailData?.data?.candidate;
    const exam = examDetailData?.data?.exam;
    const filename = `exam-detail-${candidate?.name || "unknown"}-${
      exam?.name || "exam"
    }.pdf`;
    await downloadPDF(
      `${config.API_URL}/analytics/export-exam/${selectedSessionId}`,
      filename
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: "template",
      completed: "template", 
      ongoing: "language",
      disqualified: "language",
      inactive: "language",
    };

    return (
      <ModernBadge variant={statusConfig[status] || statusConfig.inactive}>
        {status || "inactive"}
      </ModernBadge>
    );
  };

  const renderCandidatesTable = () => {
    const candidates = candidatesData?.data?.candidates || [];
    const pagination = candidatesData?.data?.pagination || {};

    // Sort the candidates
    const sortedCandidates = sortCandidates(candidates, sortField, sortOrder);

    return (
      <>

        {candidatesLoading ? (
          <ModernLoading text="Loading candidates..." />
        ) : candidatesError ? (
          <div className="text-center p-6 text-red-500">
            Error loading candidates. Please try again.
          </div>
        ) : sortedCandidates.length > 0 ? (
          <>
            {/* Candidates Table */}
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">S.No</th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Name
                        {getSortIcon('name')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Email
                        {getSortIcon('email')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('createdBy.name')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Created By
                        {getSortIcon('createdBy.name')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Created
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCandidates.map((candidate, index) => (
                    <tr key={candidate._id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {isPaginationEnabled ? (currentPage - 1) * rowsPerPage + (index + 1) : index + 1}
                      </td>
                      <td className="admin-table-cell font-medium">
                        <div className="flex items-center">
                          <User className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                          <span className="text-sm" title={candidate.name}>
                            {candidate.name}
                          </span>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm" title={candidate.email}>
                          {candidate.email}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        {candidate.createdBy ? (
                          <ModernBadge variant="language">
                            <span className="text-xs" title={candidate.createdBy.name}>
                              {candidate.createdBy.name}
                            </span>
                          </ModernBadge>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="admin-table-cell">
                        {getStatusBadge(candidate.status)}
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-xs">
                          {format(new Date(candidate.createdAt), "MMM dd, yy")}
                        </span>
                      </td>
                      <td className="admin-table-cell text-right">
                        <div className="admin-table-actions">
                          <button
                            className="admin-table-action-btn view"
                            onClick={() => handleCandidateView(candidate._id)}
                            title="View candidate details"
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No candidates found
          </div>
        )}
      </>
    );
  };

  const renderCandidateOverview = () => {
    if (detailsLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }

    if (detailsError || !candidateDetails?.data) {
      return (
        <div className="text-center p-6 text-red-500 text-sm">
          Failed to load candidate details. Please try again.
        </div>
      );
    }

    const { candidate, stats, examResults } = candidateDetails.data.data;

    // Pagination for exam results
    const totalExamPages = Math.ceil(examResults.length / EXAMS_PER_PAGE);
    const startIndex = (examResultsPage - 1) * EXAMS_PER_PAGE;
    const paginatedResults = examResults.slice(
      startIndex,
      startIndex + EXAMS_PER_PAGE
    );

    return (
      <div className="space-y-4">
        {/* Compact Candidate Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <CardHeader className="pb-2 p-0">
              <CardTitle className="text-base">Candidate Information</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium">{candidate.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="truncate ml-2">{candidate.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mobile:</span>
                  <span>{candidate.mobileNumber || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status:</span>
                  {getStatusBadge(candidate.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Registered:</span>
                  <span className="text-xs">
                    {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="p-4">
            <CardHeader className="pb-2 p-0">
              <CardTitle className="text-base">
                Performance Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-primary">
                    {stats.totalExams}
                  </div>
                  <div className="text-gray-500">Exams</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-primary">
                    {stats.avgScore}%
                  </div>
                  <div className="text-gray-500">Avg Score</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-green-600">
                    {stats.bestScore}%
                  </div>
                  <div className="text-gray-500">Best</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-orange-500">
                    {stats.worstScore}%
                  </div>
                  <div className="text-gray-500">Lowest</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Compact Exam History */}
        <Card className="p-4">
          <CardHeader className="pb-2 p-0 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Exam History</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportReport}
              disabled={isDownloading}
              className="h-7 px-2 text-xs"
            >
              {isDownloading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Download className="w-3 h-3 mr-1" />
              )}
              Export
            </Button>
          </CardHeader>
          <CardContent className="p-0 pt-2">
            {examResults && examResults.length > 0 ? (
              <div className="space-y-3">
                <div className="border rounded text-sm">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="h-8">Exam</TableHead>
                        <TableHead className="h-8">Date</TableHead>
                        <TableHead className="h-8">Score</TableHead>
                        <TableHead className="h-8">Q&A</TableHead>
                        <TableHead className="h-8 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedResults.map((result) => (
                        <TableRow key={result.resultId} className="text-xs">
                          <TableCell className="font-medium py-2">
                            <div
                              className="truncate max-w-32"
                              title={result.examName}
                            >
                              {result.examName}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-gray-400" />
                              {format(new Date(result.dateCompleted), "MMM dd")}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge
                              variant={
                                result.score >= 70
                                  ? "default"
                                  : result.score >= 40
                                  ? "secondary"
                                  : "destructive"
                              }
                              className="text-xs px-1"
                            >
                              {result.score}%
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            {result.correctAnswers}/{result.totalQuestions}
                          </TableCell>
                          <TableCell className="text-right py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleExamSelect(result.examId, result.resultId)
                              }
                              className="h-6 w-6 p-0"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Compact Pagination for exam results */}
                {totalExamPages > 1 && (
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-500">
                      {startIndex + 1}-
                      {Math.min(
                        startIndex + EXAMS_PER_PAGE,
                        examResults.length
                      )}{" "}
                      of {examResults.length}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setExamResultsPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={examResultsPage <= 1}
                        className="h-6 px-2"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <span className="text-gray-500">
                        {examResultsPage}/{totalExamPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setExamResultsPage((prev) =>
                            Math.min(totalExamPages, prev + 1)
                          )
                        }
                        disabled={examResultsPage >= totalExamPages}
                        className="h-6 px-2"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4 text-gray-500 text-sm">
                No exam history available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <ModernPageTemplate
      title="Analytics"
      contentClassName="analytics-page"
      stats={{
        total: candidatesData?.data?.pagination?.total || 0
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search candidates by name or email...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      filter={{
        onFilterClick: handleFilterToggle,
        isActive: showFilterModal || filterType !== 'all'
      }}
    >
      {renderCandidatesTable()}

      {/* Reusable Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: candidatesData?.data?.pagination?.pages || 1,
          total: candidatesData?.data?.pagination?.total || 0
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      {/* Modal for Candidate Overview */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-7xl min-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                {showExamDetail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToOverview}
                    className="h-6 px-2"
                  >
                    <ArrowLeft className="h-3 w-3" />
                  </Button>
                )}
                Candidate Analytics
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            {showExamDetail ? (
              <ExamDetailView
                sessionData={examDetailData?.data}
                onExportPDF={handleExamDetailExport}
                isLoading={examDetailLoading}
                isDownloading={isDownloading}
              />
            ) : (
              renderCandidateOverview()
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Filters
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-6">
            {/* Filter by Candidate Type */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">
                Filter by Candidate Type
              </label>
              <Select value={tempFilterType} onValueChange={setTempFilterType}>
                <SelectTrigger className="w-full h-12 border border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-gray-200">
                  <SelectValue placeholder="Select candidate type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Candidates</SelectItem>
                  <SelectItem value="my">My Candidates</SelectItem>
                  <SelectItem value="company">Company Candidates</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex justify-between items-center pt-6 border-t">
            <Button
              variant="ghost"
              onClick={handleClearFilters}
              className="text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </Button>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleCancelFilters}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyFilters}
                className="bg-violet-600 hover:bg-violet-700 text-white px-6"
              >
                Apply Filters
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ModernPageTemplate>
  );
};

export default Analytics;
